import React, { useState } from "react";
import { Upload, Button, Table, Form, notification, Spin, Typography, Card, Col, Alert } from "antd";
import { ImportOutlined, UploadOutlined } from "@ant-design/icons";
import { coreApi } from "../../api/calls";

const ImportCustomCsv = () => {
  const [fileData, setFileData] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const { Text } = Typography;

  const parseCsv = (content) => {
    const lines = content.trim().split("\n");
    const headers = lines[0].split(",");
    const rows = lines.slice(1, 11).map((line, index) => {
      const values = line.split(",");
      const row = { key: index };
      headers.forEach((header, idx) => {
        row[header] = values[idx] || "";
      });
      return row;
    });
    setFileData(rows);
  };

  const sanitizeFileName = (name) => {
    return name
      .replace(/[^a-zA-Z0-9._-]/g, "_")
      .replace(/_{2,}/g, "_");
  };

  const handleUpload = (file) => {
    const sanitizedFileName = sanitizeFileName(file.name);
    const renamedFile = new File([file], sanitizedFileName, { type: file.type });
    setUploadedFile(renamedFile);

    const reader = new FileReader();
    reader.onload = (e) => {
      parseCsv(e.target.result);
      setLoading(false);
    };
    reader.readAsText(renamedFile);
    return false;
  };

  const handleSubmit = async () => {
    if (!uploadedFile) {
      notification.error({ message: "Please upload a CSV file." });
      return;
    }
    setErrorMessage(null);
    setSubmitting(true);
    const formData = new FormData();
    formData.append("file", uploadedFile);

    try {
      const response = await coreApi.post("/import/mailing-database", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: response.headers["content-type"] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const disposition = response.headers["content-disposition"];
      let fileName = "download.csv";
      if (disposition && disposition.includes("filename=")) {
        fileName = disposition.split("filename=")[1].replace(/"/g, "");
      }

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      notification.success({ message: "File imported successfully!" });
      handleClose();
    } catch (error) {
      console.error("Import error:", error);

      let message = "File import failed. Please try again.";

      if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
        message = "Timeout: The server took too long to respond.";
      } else if (!error.response) {
        message = "Network error: Could not connect to the server.";
      } else if (error.response?.status >= 500) {
        message = "Server error: Please try again later.";
      } else if (error.response?.data?.message) {
        message = error.response.data.message;
      }

      setErrorMessage(message);
      notification.error({ message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setUploadedFile(null);
    setFileData([]);
    setErrorMessage(null);
  };

  const columns = fileData.length
    ? Object.keys(fileData[0]).map((key) => ({
      title: key,
      dataIndex: key,
      key,
      ellipsis: true,
    }))
    : [];
  return (
    <Col span={24}>
      <Card title="Mailing Database Script">
        <Form layout="vertical">
          <div style={{ display: "flex", gap: "10px" }}>
            <Upload
              maxCount={1}
              accept=".csv"
              beforeUpload={handleUpload}
              onRemove={() => {
                setUploadedFile(null);
                setFileData([]);
                setErrorMessage(null);
              }}
            >
              <Button icon={<UploadOutlined />}>Upload CSV</Button>
            </Upload>
            {loading && <Spin size="large" />}
            {uploadedFile && <Button
              type="primary"
              style={{marginLeft: "10px"}}
              icon={<ImportOutlined />}
              onClick={handleSubmit}
              disabled={!uploadedFile}
              loading={submitting}
            >
              Import
            </Button>}
          </div>
          {errorMessage && (
            <Alert
              message={errorMessage}
              type="error"
              showIcon
              style={{ marginTop: 12, maxWidth: 400 }}
            />
          )}
          {!loading && fileData.length > 0 && uploadedFile && uploadedFile.name.toLowerCase().endsWith('.csv') && (
            <>
              <Table
                dataSource={fileData}
                columns={columns}
                pagination={false}
                scroll={{ y: 240, x: "max-content" }}
                style={{ marginTop: 16 }}
              />
              <Text style={{ display: "block", marginTop: 8 }}>
                Showing {fileData.length} rows
              </Text>
            </>
          )}
        </Form>
      </Card>
    </Col>
  );
};

export default ImportCustomCsv;
